/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  BillingDetailsDto,
  CheckoutSessionDto,
  ChooseSubscriptionDto,
  InvoiceUrlDto,
  PlanDto,
  SubscriptionDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary The plans a workspace can choose, cheapest first.
 */
export const listPlans = (signal?: AbortSignal) => {
  return customInstance<PlanDto[]>({ url: `/api/v1/plans`, method: 'GET', signal });
};

export const getListPlansQueryKey = () => {
  return [`/api/v1/plans`] as const;
};

export const getListPlansQueryOptions = <
  TData = Awaited<ReturnType<typeof listPlans>>,
  TError = ErrorType<ApiErrorDto>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listPlans>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListPlansQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlans>>> = ({ signal }) =>
    listPlans(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listPlans>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListPlansQueryResult = NonNullable<Awaited<ReturnType<typeof listPlans>>>;
export type ListPlansQueryError = ErrorType<ApiErrorDto>;

export function useListPlans<
  TData = Awaited<ReturnType<typeof listPlans>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPlans>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPlans>>,
          TError,
          Awaited<ReturnType<typeof listPlans>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPlans<
  TData = Awaited<ReturnType<typeof listPlans>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPlans>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPlans>>,
          TError,
          Awaited<ReturnType<typeof listPlans>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPlans<
  TData = Awaited<ReturnType<typeof listPlans>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPlans>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The plans a workspace can choose, cheapest first.
 */

export function useListPlans<
  TData = Awaited<ReturnType<typeof listPlans>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listPlans>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListPlansQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary The workspace's plan and trial. 404 until a plan is chosen.
 */
export const getSubscription = (id: string, signal?: AbortSignal) => {
  return customInstance<SubscriptionDto>({
    url: `/api/v1/workspaces/${id}/subscription`,
    method: 'GET',
    signal,
  });
};

export const getGetSubscriptionQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/subscription`] as const;
};

export const getGetSubscriptionQueryOptions = <
  TData = Awaited<ReturnType<typeof getSubscription>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSubscription>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSubscriptionQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSubscription>>> = ({
    signal,
  }) => getSubscription(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getSubscription>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetSubscriptionQueryResult = NonNullable<
  Awaited<ReturnType<typeof getSubscription>>
>;
export type GetSubscriptionQueryError = ErrorType<ApiErrorDto>;

export function useGetSubscription<
  TData = Awaited<ReturnType<typeof getSubscription>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSubscription>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSubscription>>,
          TError,
          Awaited<ReturnType<typeof getSubscription>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSubscription<
  TData = Awaited<ReturnType<typeof getSubscription>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSubscription>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSubscription>>,
          TError,
          Awaited<ReturnType<typeof getSubscription>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSubscription<
  TData = Awaited<ReturnType<typeof getSubscription>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSubscription>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The workspace's plan and trial. 404 until a plan is chosen.
 */

export function useGetSubscription<
  TData = Awaited<ReturnType<typeof getSubscription>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSubscription>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSubscriptionQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Choose or change the workspace's plan. Before checkout it's only saved;
after, the provider changes it (prorated). With payments off, the first
choice starts a local free trial.
 */
export const chooseSubscription = (
  id: string,
  chooseSubscriptionDto: BodyType<ChooseSubscriptionDto>,
  signal?: AbortSignal,
) => {
  return customInstance<SubscriptionDto>({
    url: `/api/v1/workspaces/${id}/subscription`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: chooseSubscriptionDto,
    signal,
  });
};

export const getChooseSubscriptionMutationKey = () => ['chooseSubscription'] as const;

export const getChooseSubscriptionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof chooseSubscription>>,
    TError,
    ChooseSubscriptionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof chooseSubscription>>,
  TError,
  ChooseSubscriptionMutationVariables,
  TContext
> => {
  const mutationKey = getChooseSubscriptionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof chooseSubscription>>,
    ChooseSubscriptionMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return chooseSubscription(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ChooseSubscriptionMutationResult = NonNullable<
  Awaited<ReturnType<typeof chooseSubscription>>
>;
export type ChooseSubscriptionMutationBody = BodyType<ChooseSubscriptionDto>;
export type ChooseSubscriptionMutationError = ErrorType<ApiErrorDto>;
export type ChooseSubscriptionMutationVariables = {
  id: string;
  data: BodyType<ChooseSubscriptionDto>;
};

/**
 * @summary Choose or change the workspace's plan. Before checkout it's only saved;
after, the provider changes it (prorated). With payments off, the first
choice starts a local free trial.
 */
export const useChooseSubscription = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof chooseSubscription>>,
      TError,
      ChooseSubscriptionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof chooseSubscription>>,
  TError,
  ChooseSubscriptionMutationVariables,
  TContext
> => {
  return useMutation(getChooseSubscriptionMutationOptions(options), queryClient);
};
/**
 * @summary Opens the payment provider's checkout for the chosen plan, starting its trial.
 */
export const createCheckout = (id: string, signal?: AbortSignal) => {
  return customInstance<CheckoutSessionDto>({
    url: `/api/v1/workspaces/${id}/subscription/checkout`,
    method: 'POST',
    signal,
  });
};

export const getCreateCheckoutMutationKey = () => ['createCheckout'] as const;

export const getCreateCheckoutMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createCheckout>>,
    TError,
    CreateCheckoutMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createCheckout>>,
  TError,
  CreateCheckoutMutationVariables,
  TContext
> => {
  const mutationKey = getCreateCheckoutMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createCheckout>>,
    CreateCheckoutMutationVariables
  > = (props) => {
    const { id } = props ?? {};

    return createCheckout(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateCheckoutMutationResult = NonNullable<
  Awaited<ReturnType<typeof createCheckout>>
>;

export type CreateCheckoutMutationError = ErrorType<ApiErrorDto>;
export type CreateCheckoutMutationVariables = { id: string };

/**
 * @summary Opens the payment provider's checkout for the chosen plan, starting its trial.
 */
export const useCreateCheckout = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createCheckout>>,
      TError,
      CreateCheckoutMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createCheckout>>,
  TError,
  CreateCheckoutMutationVariables,
  TContext
> => {
  return useMutation(getCreateCheckoutMutationOptions(options), queryClient);
};
/**
 * @summary Opens a checkout that replaces the card the subscription is charged to.
 */
export const createPaymentMethodCheckout = (id: string, signal?: AbortSignal) => {
  return customInstance<CheckoutSessionDto>({
    url: `/api/v1/workspaces/${id}/subscription/payment-method`,
    method: 'POST',
    signal,
  });
};

export const getCreatePaymentMethodCheckoutMutationKey = () =>
  ['createPaymentMethodCheckout'] as const;

export const getCreatePaymentMethodCheckoutMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPaymentMethodCheckout>>,
    TError,
    CreatePaymentMethodCheckoutMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPaymentMethodCheckout>>,
  TError,
  CreatePaymentMethodCheckoutMutationVariables,
  TContext
> => {
  const mutationKey = getCreatePaymentMethodCheckoutMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPaymentMethodCheckout>>,
    CreatePaymentMethodCheckoutMutationVariables
  > = (props) => {
    const { id } = props ?? {};

    return createPaymentMethodCheckout(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePaymentMethodCheckoutMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPaymentMethodCheckout>>
>;

export type CreatePaymentMethodCheckoutMutationError = ErrorType<ApiErrorDto>;
export type CreatePaymentMethodCheckoutMutationVariables = { id: string };

/**
 * @summary Opens a checkout that replaces the card the subscription is charged to.
 */
export const useCreatePaymentMethodCheckout = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createPaymentMethodCheckout>>,
      TError,
      CreatePaymentMethodCheckoutMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createPaymentMethodCheckout>>,
  TError,
  CreatePaymentMethodCheckoutMutationVariables,
  TContext
> => {
  return useMutation(getCreatePaymentMethodCheckoutMutationOptions(options), queryClient);
};
/**
 * @summary Cancels at the end of the current period (or trial); until then nothing changes.
 */
export const cancelSubscription = (id: string, signal?: AbortSignal) => {
  return customInstance<SubscriptionDto>({
    url: `/api/v1/workspaces/${id}/subscription/cancel`,
    method: 'POST',
    signal,
  });
};

export const getCancelSubscriptionMutationKey = () => ['cancelSubscription'] as const;

export const getCancelSubscriptionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof cancelSubscription>>,
    TError,
    CancelSubscriptionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof cancelSubscription>>,
  TError,
  CancelSubscriptionMutationVariables,
  TContext
> => {
  const mutationKey = getCancelSubscriptionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof cancelSubscription>>,
    CancelSubscriptionMutationVariables
  > = (props) => {
    const { id } = props ?? {};

    return cancelSubscription(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type CancelSubscriptionMutationResult = NonNullable<
  Awaited<ReturnType<typeof cancelSubscription>>
>;

export type CancelSubscriptionMutationError = ErrorType<ApiErrorDto>;
export type CancelSubscriptionMutationVariables = { id: string };

/**
 * @summary Cancels at the end of the current period (or trial); until then nothing changes.
 */
export const useCancelSubscription = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof cancelSubscription>>,
      TError,
      CancelSubscriptionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof cancelSubscription>>,
  TError,
  CancelSubscriptionMutationVariables,
  TContext
> => {
  return useMutation(getCancelSubscriptionMutationOptions(options), queryClient);
};
/**
 * @summary Takes back a cancellation before it happens.
 */
export const resumeSubscription = (id: string, signal?: AbortSignal) => {
  return customInstance<SubscriptionDto>({
    url: `/api/v1/workspaces/${id}/subscription/resume`,
    method: 'POST',
    signal,
  });
};

export const getResumeSubscriptionMutationKey = () => ['resumeSubscription'] as const;

export const getResumeSubscriptionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof resumeSubscription>>,
    TError,
    ResumeSubscriptionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof resumeSubscription>>,
  TError,
  ResumeSubscriptionMutationVariables,
  TContext
> => {
  const mutationKey = getResumeSubscriptionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof resumeSubscription>>,
    ResumeSubscriptionMutationVariables
  > = (props) => {
    const { id } = props ?? {};

    return resumeSubscription(id);
  };

  return { mutationFn, ...mutationOptions };
};

export type ResumeSubscriptionMutationResult = NonNullable<
  Awaited<ReturnType<typeof resumeSubscription>>
>;

export type ResumeSubscriptionMutationError = ErrorType<ApiErrorDto>;
export type ResumeSubscriptionMutationVariables = { id: string };

/**
 * @summary Takes back a cancellation before it happens.
 */
export const useResumeSubscription = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof resumeSubscription>>,
      TError,
      ResumeSubscriptionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof resumeSubscription>>,
  TError,
  ResumeSubscriptionMutationVariables,
  TContext
> => {
  return useMutation(getResumeSubscriptionMutationOptions(options), queryClient);
};
/**
 * @summary The card, billing email and invoices, from the payment provider.
 */
export const getBillingDetails = (id: string, signal?: AbortSignal) => {
  return customInstance<BillingDetailsDto>({
    url: `/api/v1/workspaces/${id}/subscription/billing`,
    method: 'GET',
    signal,
  });
};

export const getGetBillingDetailsQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/subscription/billing`] as const;
};

export const getGetBillingDetailsQueryOptions = <
  TData = Awaited<ReturnType<typeof getBillingDetails>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBillingDetails>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBillingDetailsQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBillingDetails>>> = ({
    signal,
  }) => getBillingDetails(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getBillingDetails>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetBillingDetailsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBillingDetails>>
>;
export type GetBillingDetailsQueryError = ErrorType<ApiErrorDto>;

export function useGetBillingDetails<
  TData = Awaited<ReturnType<typeof getBillingDetails>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBillingDetails>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBillingDetails>>,
          TError,
          Awaited<ReturnType<typeof getBillingDetails>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBillingDetails<
  TData = Awaited<ReturnType<typeof getBillingDetails>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBillingDetails>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBillingDetails>>,
          TError,
          Awaited<ReturnType<typeof getBillingDetails>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBillingDetails<
  TData = Awaited<ReturnType<typeof getBillingDetails>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBillingDetails>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The card, billing email and invoices, from the payment provider.
 */

export function useGetBillingDetails<
  TData = Awaited<ReturnType<typeof getBillingDetails>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBillingDetails>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetBillingDetailsQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary A short-lived link to one invoice's PDF.
 */
export const getInvoiceUrl = (id: string, invoiceId: string, signal?: AbortSignal) => {
  return customInstance<InvoiceUrlDto>({
    url: `/api/v1/workspaces/${id}/subscription/invoices/${invoiceId}/pdf`,
    method: 'GET',
    signal,
  });
};

export const getGetInvoiceUrlQueryKey = (id: string, invoiceId: string) => {
  return [`/api/v1/workspaces/${id}/subscription/invoices/${invoiceId}/pdf`] as const;
};

export const getGetInvoiceUrlQueryOptions = <
  TData = Awaited<ReturnType<typeof getInvoiceUrl>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  invoiceId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getInvoiceUrl>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInvoiceUrlQueryKey(id, invoiceId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getInvoiceUrl>>> = ({
    signal,
  }) => getInvoiceUrl(id, invoiceId, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      id !== null && id !== undefined && invoiceId !== null && invoiceId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getInvoiceUrl>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetInvoiceUrlQueryResult = NonNullable<
  Awaited<ReturnType<typeof getInvoiceUrl>>
>;
export type GetInvoiceUrlQueryError = ErrorType<ApiErrorDto>;

export function useGetInvoiceUrl<
  TData = Awaited<ReturnType<typeof getInvoiceUrl>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  invoiceId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getInvoiceUrl>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInvoiceUrl>>,
          TError,
          Awaited<ReturnType<typeof getInvoiceUrl>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetInvoiceUrl<
  TData = Awaited<ReturnType<typeof getInvoiceUrl>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  invoiceId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getInvoiceUrl>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInvoiceUrl>>,
          TError,
          Awaited<ReturnType<typeof getInvoiceUrl>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetInvoiceUrl<
  TData = Awaited<ReturnType<typeof getInvoiceUrl>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  invoiceId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getInvoiceUrl>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary A short-lived link to one invoice's PDF.
 */

export function useGetInvoiceUrl<
  TData = Awaited<ReturnType<typeof getInvoiceUrl>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  invoiceId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getInvoiceUrl>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetInvoiceUrlQueryOptions(id, invoiceId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}
